import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GraphCanvas } from '../components/GraphCanvas';
import type { GraphCanvasRef } from '../components/GraphCanvas';
import { transactionService } from '../services/api';
import { Copy, ExternalLink, AlertTriangle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export const Transaction: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [txDetails, setTxDetails] = useState<any>(null);
    const [elements, setElements] = useState<any[]>([]);
    const graphRef = React.useRef<GraphCanvasRef>(null);

    const txHash = searchParams.get('tx') || '';
    const chainParam = searchParams.get('chain') || '';

    useEffect(() => {
        const loadTransaction = async () => {
            if (!txHash) return;

            setLoading(true);
            try {
                let details = null;
                if (chainParam) {
                    details = await transactionService.getTransaction(chainParam, txHash);
                } else {
                    details = await transactionService.searchTransaction(txHash);
                }

                const resolvedChain = details?.chain || chainParam || transactionService.detectChainFromHash(txHash) || 'ETH';
                const graph = await transactionService.getTransactionGraph(resolvedChain, txHash);

                setTxDetails(details);

                if (graph?.nodes && graph?.edges) {
                    const newElements: any[] = [];

                    graph.nodes.forEach((node: any) => {
                        newElements.push({
                            data: {
                                id: node.id,
                                label: node.label || node.id.substring(0, 8) + '...',
                                type: 'address',
                                entity_name: node.entity_name,
                                entity_type: node.entity_type,
                                risk_score: node.risk_score
                            }
                        });
                    });

                    graph.edges.forEach((edge: any) => {
                        newElements.push({
                            data: {
                                id: edge.id,
                                source: edge.source,
                                target: edge.target,
                                label: `${edge.value} ${edge.token_symbol || ''}`,
                                tx_hash: edge.tx_hash
                            }
                        });
                    });

                    setElements(newElements);
                }
            } catch (e: any) {
                console.error('Failed to load transaction', e);
                toast.error('Failed to load transaction details');
            } finally {
                setLoading(false);
            }
        };

        loadTransaction();
    }, [txHash, chainParam]);

    if (!txHash) {
        return (
            <div className="p-8 text-center text-muted-foreground">
                <p>No transaction hash provided.</p>
            </div>
        );
    }

    const explorerUrl = transactionService.getExplorerUrl(txDetails?.chain || chainParam || 'ETH', txHash);

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">Transaction Details</h1>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText(txHash);
                            toast.success('Transaction hash copied!');
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded hover:bg-muted"
                    >
                        <Copy className="w-4 h-4" /> Copy Hash
                    </button>
                    <a
                        href={explorerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded hover:bg-muted"
                    >
                        <ExternalLink className="w-4 h-4" /> Explorer
                    </a>
                </div>
            </div>

            {/* Resource-operation banner — a real Tron tx that moves no funds
                (Freeze/Un-/DelegateResource/VoteWitness/etc.). The transfer-
                centric indexer 404s on these; the backend now resolves them via
                the full node and flags is_value_transfer=false so we can say so
                explicitly instead of showing an empty/zero transfer. */}
            {txDetails?.is_value_transfer === false && (
                <div className="flex items-start gap-2 px-4 py-3 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                        <div className="font-semibold">Resource operation — no value transfer</div>
                        <div className="text-xs opacity-90 mt-0.5">
                            {txDetails.operation_label || txDetails.contract_type}
                            {txDetails.resource_type ? ` · ${txDetails.resource_type}` : ''}
                            {txDetails.resource_amount_trx ? ` · ${txDetails.resource_amount_trx.toLocaleString()} TRX` : ''}
                            {'. '}This Tron transaction is a staking / governance action and does not move funds, so it has no transfer amount or counterparty.
                        </div>
                    </div>
                </div>
            )}

            {/* Transaction Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card rounded border border-border p-4 space-y-2">
                    <div className="text-xs text-muted-foreground">Transaction Hash</div>
                    <div className="font-mono text-sm break-all">{txHash}</div>

                    <div className="text-xs text-muted-foreground mt-2">Chain</div>
                    <div>{txDetails?.chain || chainParam || 'Unknown'}</div>

                    <div className="text-xs text-muted-foreground mt-2">Timestamp</div>
                    <div>{txDetails?.timestamp ? new Date(txDetails.timestamp).toLocaleString() : 'Unknown'}</div>

                    <div className="text-xs text-muted-foreground mt-2">Block Height</div>
                    <div>{txDetails?.block_height || 'Unknown'}</div>
                </div>

                <div className="bg-card rounded border border-border p-4 space-y-2">
                    <div className="flex items-center gap-2">
                        <div className="text-xs text-muted-foreground">From</div>
                        {txDetails?.from_entity_type && (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-primary/10 text-primary">
                                {txDetails.from_entity_type}
                            </span>
                        )}
                    </div>
                    <div className="font-mono text-sm break-all">{txDetails?.from_address || 'Unknown'}</div>

                    <div className="flex items-center gap-2 mt-2">
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Value</span>
                        <span className="font-semibold">
                            {txDetails?.value?.toLocaleString?.() || txDetails?.value || '0'} {txDetails?.token_symbol || ''}
                        </span>
                    </div>

                    <div className="flex items-center gap-2 mt-2">
                        <div className="text-xs text-muted-foreground">To</div>
                        {txDetails?.to_entity_type && (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-green-100 text-green-700">
                                {txDetails.to_entity_type}
                            </span>
                        )}
                    </div>
                    <div className="font-mono text-sm break-all">{txDetails?.to_address || 'Unknown'}</div>

                    {(txDetails?.from_risk_score || txDetails?.to_risk_score) && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-red-600">
                            <AlertTriangle className="w-3 h-3" />
                            One or more parties flagged as high risk
                        </div>
                    )}
                </div>
            </div>

            {/* Graph Visualization */}
            <div className="bg-card rounded border border-border p-4">
                <h2 className="text-sm font-semibold mb-3">Transaction Graph</h2>
                {loading && <div className="text-xs text-muted-foreground">Loading graph...</div>}
                {!loading && elements.length === 0 && (
                    <div className="text-xs text-muted-foreground">No graph data available.</div>
                )}
                <div className="h-[400px]">
                    <GraphCanvas ref={graphRef} elements={elements} />
                </div>
            </div>
        </div>
    );
};

export default Transaction;
